import * as React from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useState, useEffect } from "react";
import { toasty } from "../../../../../../Helper";
import apiCall from "../../../../../API/apiService";
import FeedbackIcon from '@mui/icons-material/Feedback';
import BugReportIcon from '@mui/icons-material/BugReport';

const Metrix = ({switchToApplicantsTab}) => {
  const [metricsData, setMetricsData] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [selectedRound, setSelectedRound] = useState(null);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueData, setIssueData] = useState(null);
  const [loadingIssues, setLoadingIssues] = useState(false);

  useEffect(() => {
    const url = window.location.href;
    const pathSegments = url.split("/");
    const id = pathSegments[4];
    if (id) {
    fetchMetricsData(id);
    }
  }, []);

  const fetchMetricsData = async (id) => {
    setLoadingMetrics(true);
    try {
      const response = await apiCall("get", `/api/applicants/job_opening/${id}/metrics`);
      setMetricsData(response);
    } catch (error) {
      console.error("Error fetching metrics data:", error);
      toasty("Error fetching metrics data", "error");
    } finally {
      setLoadingMetrics(false);
    }
  };

  const fetchFeedbackData = async (roundNumber) => {
    setLoadingFeedback(true);
    try {
      const url = window.location.href;
      const pathSegments = url.split("/");
      const id = pathSegments[4];
      const response = await apiCall("get", `/api/jobinterviews/feedback-distribution/${id}/round/${roundNumber}`);
      setFeedbackData(response);
    } catch (error) {
      console.error("Error fetching feedback data:", error);
      toasty("Error fetching feedback data", "error");
    } finally {
      setLoadingFeedback(false);
    }
  };

  const fetchIssueData = async (roundNumber) => {
    setLoadingIssues(true);
    try {
      const url = window.location.href;
      const pathSegments = url.split("/");
      const id = pathSegments[4];
      const response = await apiCall("get", `/api/jobinterviews/issue-distribution/${id}/round/${roundNumber}`);
      setIssueData(response);
    } catch (error) {
      console.error("Error fetching issue data:", error);
      toasty("Error fetching issue data", "error");
    } finally {
      setLoadingIssues(false);
    }
  };

  const handleStatusCardClick = (roundNumber, status) => {
    if (switchToApplicantsTab) {
      switchToApplicantsTab(roundNumber, status);
    }
  };

  const handleFeedbackClick = (roundNumber) => {
    setSelectedRound(roundNumber);
    setFeedbackDialogOpen(true);
    fetchFeedbackData(roundNumber);
  };

  const handleIssueClick = (roundNumber) => {
    setSelectedRound(roundNumber);
    setIssueDialogOpen(true);
    fetchIssueData(roundNumber);
  };

  const renderFeedbackDialog = () => {
    return (
      <Dialog 
        open={feedbackDialogOpen} 
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: "var(--Blue-700, rgba(29, 78, 216, 1))",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          Feedback Distribution - Round {selectedRound}
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          {loadingFeedback ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : feedbackData ? (
            <Box>
              <Grid container spacing={2}>
                {Object.entries(feedbackData.feedback_distribution).map(([rating, count]) => (
                  <Grid item xs={4} key={rating}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: "var(--Blue-50, rgba(239, 246, 255, 1))",
                        textAlign: "center",
                        border: "1px solid var(--Blue-200, rgba(191, 219, 254, 1))",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "var(--Blue-700, rgba(29, 78, 216, 1))",
                          fontWeight: 600,
                        }}
                      >
                        {count}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "var(--Gray-700, rgba(55, 65, 81, 1))",
                        }}
                      >
                        {rating === "NA" ? "Not Rated" : `${rating} Star${rating === "1" ? "" : "s"}`}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Typography variant="body2" color="error">
              No feedback data available
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderIssueDialog = () => {
    return (
      <Dialog 
        open={issueDialogOpen} 
        onClose={() => setIssueDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: "var(--Blue-700, rgba(29, 78, 216, 1))",
          fontWeight: 600,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          Issue Distribution - Round {selectedRound}
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          {loadingIssues ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : issueData ? (
            <Box>
              <Grid container spacing={2}>
                {Object.entries(issueData.issue_distribution).map(([issue, count]) => (
                  <Grid item xs={4} key={issue}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: "var(--Blue-50, rgba(239, 246, 255, 1))",
                        textAlign: "center",
                        border: "1px solid var(--Blue-200, rgba(191, 219, 254, 1))",
                        height: '100px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: "var(--Blue-700, rgba(29, 78, 216, 1))",
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        {count}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "var(--Gray-700, rgba(55, 65, 81, 1))",
                          textAlign: 'center',
                          wordBreak: 'break-word'
                        }}
                      >
                        {issue.replace(/_/g, " ")}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Typography variant="body2" color="error">
              No issue data available
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
        <DialogTitle
          sx={{
            // backgroundColor: "var(--Blue-50, rgba(239, 246, 255, 1))",
            color: "var(--Blue-700, rgba(29, 78, 216, 1))",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Applicant Metrics
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 2 }}>
          {loadingMetrics ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : metricsData ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: "var(--Blue-50, rgba(239, 246, 255, 1))",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: "var(--Blue-700, rgba(29, 78, 216, 1))",
                    mb: 0.5,
                  }}
                >
                  {metricsData.total_applicants || 0}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "var(--Gray-700, rgba(55, 65, 81, 1))" }}
                >
                  Total Applicants
                </Typography>
              </Box>

              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                  color: "var(--Gray-900, rgba(17, 24, 39, 1))",
                }}
              >
                Round Status Breakdown
              </Typography>

              {metricsData.round_metrics &&
                Object.entries(metricsData.round_metrics).map(
                  ([roundNumber, statuses]) => (
                    <Box key={roundNumber} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600 }}
                        >
                          Round {roundNumber}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Issue Distribution">
                            <IconButton 
                              onClick={() => handleIssueClick(roundNumber)}
                              size="small"
                              sx={{ 
                                color: "var(--Blue-600, rgba(37, 99, 235, 1))",
                                '&:hover': {
                                  backgroundColor: "var(--Blue-50, rgba(239, 246, 255, 1))"
                                }
                              }}
                            >
                              <BugReportIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Feedback Distribution">
                            <IconButton 
                              onClick={() => handleFeedbackClick(roundNumber)}
                              size="small"
                              sx={{ 
                                color: "var(--Blue-600, rgba(37, 99, 235, 1))",
                                '&:hover': {
                                  backgroundColor: "var(--Blue-50, rgba(239, 246, 255, 1))"
                                }
                              }}
                            >
                              <FeedbackIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                      <Grid container spacing={1}>
                        {Object.entries(statuses).map(([status, count]) => (
                          <Grid item xs={6} sm={4} key={status}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                backgroundColor:
                                  "var(--Gray-50, rgba(249, 250, 251, 1))",
                                border:
                                  "1px solid var(--Gray-200, rgba(229, 231, 235, 1))",
                                textAlign: "center",
                                "&:hover": {
                                  backgroundColor:
                                    "var(--Gray-100, rgba(243, 244, 246, 1))",
                                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                                },
                              }}
                              onClick={() =>
                                handleStatusCardClick(roundNumber, status)
                              }
                            >
                              <Typography
                                variant="h6"
                                sx={{
                                  color:
                                    "var(--Blue-600, rgba(37, 99, 235, 1))",
                                  fontWeight: 600,
                                  fontSize: "1.1rem",
                                }}
                              >
                                {count}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "var(--Gray-700, rgba(55, 65, 81, 1))",
                                }}
                              >
                                {status.replace(/_/g, " ")}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )
                )}
            </>
          ) : (
            <Typography variant="body2" color="error">
              No metrics data available
            </Typography>
          )}
        </DialogContent>
        {renderFeedbackDialog()}
        {renderIssueDialog()}
    </div>
  );
};

export default Metrix;
